import React, { useEffect, useState } from 'react';
import {
  Bell,
  Video,
  MessageCircle,
  FileText,
  CreditCard,
  Megaphone,
  Smartphone,
  Mail,
  MessageSquare,
  Inbox,
  Loader2,
} from 'lucide-react';
import {
  NOTIFICATION_CATEGORIES,
  loadNotificationPreferencesFromServer,
  saveNotificationPreferencesToServer,
} from '../../utils/notificationManager';

const CATEGORY_ICONS = {
  class: Video,
  assignment: FileText,
  chat: MessageCircle,
  payment: CreditCard,
  general: Megaphone,
};

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); NotificationPreferences.scss
// deleted with it.
//
// The toggle and the channel chips were `#6366f1` — an indigo, not `--primary` —
// in five places, so the one control on this panel that says "on" was the one
// thing that never followed the institute's brand. They are `--primary` now.
//
// The five category tiles were hand-picked hues (`#6366f1`, `#f59e0b`, `#3b82f6`,
// `#22c55e`, `#6b7280`); they read the chart ramp, which is what a fixed
// category colour is for (TOKEN_CONTRACT §4.5) and gives them a dark mode. The
// thumb was `#fff` — `--on-status`, for the Phase 7.1 reason: a light brand
// makes white unreadable, and the thumb sits on `--primary` when it is on.
const CATEGORY_TONE = {
  class: 'tw-bg-chart-1',
  assignment: 'tw-bg-chart-3',
  chat: 'tw-bg-chart-5',
  payment: 'tw-bg-chart-2',
  general: 'tw-bg-muted-fg',
};

const STATE = 'tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-p-3 tw-text-[0.82rem]';

const ITEM =
  'tw-mb-1.5 tw-flex tw-flex-col tw-items-stretch tw-gap-2 tw-rounded-sm tw-border ' +
  'tw-border-line tw-p-3';

const ITEM_ICON =
  'tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm ' +
  'tw-text-on-status';

const TRACK =
  'tw-relative tw-block tw-h-[22px] tw-w-[38px] tw-rounded-full tw-transition-colors tw-duration-200';
const THUMB =
  'tw-absolute tw-left-[3px] tw-top-[3px] tw-h-4 tw-w-4 tw-rounded-full tw-bg-on-status ' +
  'tw-shadow-card tw-transition-transform tw-duration-200';

const CHANNEL =
  'tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-full tw-border ' +
  'tw-px-2 tw-py-1 tw-text-[0.7rem] tw-font-semibold tw-transition-all tw-duration-150';
const CHANNEL_OFF =
  'tw-border-line tw-bg-transparent tw-text-content-muted hover:tw-border-primary hover:tw-text-content';
const CHANNEL_ON = 'tw-border-primary tw-bg-primary/[0.12] tw-text-primary';

const CHANNELS = [
  { key: 'inApp',    label: 'In-app',   Icon: Inbox },
  { key: 'push',     label: 'Push',     Icon: Smartphone },
  { key: 'email',    label: 'Email',    Icon: Mail },
  { key: 'whatsapp', label: 'WhatsApp', Icon: MessageSquare },
];

function emptyPrefs() {
  const p = {};
  for (const key of Object.keys(NOTIFICATION_CATEGORIES)) {
    p[key] = { inApp: true, push: true, email: true, whatsapp: true };
  }
  return p;
}

const NotificationPreferences = () => {
  const [prefs, setPrefs] = useState(emptyPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const server = await loadNotificationPreferencesFromServer();
      if (cancelled) return;
      if (server) {
        const merged = emptyPrefs();
        for (const cat of Object.keys(merged)) {
          if (server[cat]) merged[cat] = { ...merged[cat], ...server[cat] };
        }
        setPrefs(merged);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = async (next) => {
    setPrefs(next);
    setSaving(true);
    setError(null);
    try {
      await saveNotificationPreferencesToServer(next);
    } catch (err) {
      setError('Failed to save. Changes may not persist.');
    } finally {
      setSaving(false);
    }
  };

  const handleChannelToggle = (catKey, channelKey) => {
    const next = {
      ...prefs,
      [catKey]: { ...prefs[catKey], [channelKey]: !(prefs[catKey]?.[channelKey] !== false) },
    };
    persist(next);
  };

  const handleRowToggle = (catKey) => {
    const cat = prefs[catKey] || {};
    const anyOn = CHANNELS.some(c => cat[c.key] !== false);
    const newVal = !anyOn;
    const next = {
      ...prefs,
      [catKey]: CHANNELS.reduce((acc, c) => ({ ...acc, [c.key]: newVal }), {}),
    };
    persist(next);
  };

  return (
    <div>
      <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2.5 tw-text-content">
        <Bell size={20} />
        <h3 className="tw-m-0 tw-text-base tw-font-medium tw-text-content">Notification Preferences</h3>
        {saving && <Loader2 size={14} className="tw-animate-spin" />}
      </div>
      <p className="tw-mb-4 tw-mt-0 tw-text-[0.82rem] tw-text-content-muted">
        Choose which notifications you want to receive and on which channels.
        Changes are saved to your account and apply across all your devices.
      </p>
      {error && <div className={`${STATE} tw-mb-2.5 tw-bg-danger-soft tw-text-danger-fg`}>{error}</div>}

      {loading ? (
        <div className={`${STATE} tw-justify-center tw-text-content-muted`}>
          <Loader2 size={18} className="tw-animate-spin" /> Loading preferences…
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          {Object.entries(NOTIFICATION_CATEGORIES).map(([key, cat]) => {
            const IconComp = CATEGORY_ICONS[key] || Bell;
            const catPrefs = prefs[key] || {};
            const anyOn = CHANNELS.some(c => catPrefs[c.key] !== false);
            return (
              <div key={key} className={ITEM}>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2.5">
                  <div className="tw-flex tw-items-center tw-gap-2.5">
                    <div className={`${ITEM_ICON} ${CATEGORY_TONE[key] || CATEGORY_TONE.general}`}>
                      <IconComp size={16} />
                    </div>
                    <div>
                      <div className="tw-text-[0.85rem] tw-font-semibold tw-text-content">{cat.label}</div>
                      <div className="tw-text-xs tw-text-content-muted">{cat.description}</div>
                    </div>
                  </div>
                  <div className="tw-relative">
                    <input
                      className="tw-absolute tw-h-0 tw-w-0 tw-opacity-0"
                      type="checkbox"
                      checked={anyOn}
                      onChange={() => handleRowToggle(key)}
                      aria-label={`Toggle all channels for ${cat.label}`}
                    />
                    <span className={`${TRACK} ${anyOn ? "tw-bg-primary" : "tw-bg-line"}`}>
                      <span className={`${THUMB} ${anyOn ? "tw-translate-x-4" : "tw-translate-x-0"}`} />
                    </span>
                  </div>
                </div>
                <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-ps-[42px]">
                  {CHANNELS.map(({ key: chKey, label, Icon }) => {
                    const enabled = catPrefs[chKey] !== false;
                    return (
                      <button
                        key={chKey}
                        type="button"
                        className={`${CHANNEL} ${enabled ? CHANNEL_ON : CHANNEL_OFF}`}
                        onClick={() => handleChannelToggle(key, chKey)}
                        aria-pressed={enabled}
                        title={`${enabled ? 'Disable' : 'Enable'} ${label} for ${cat.label}`}
                      >
                        <Icon size={12} /> <span>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationPreferences;
